import React, { useState } from 'react';
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';

interface RequestChangesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend?: () => void;
  itineraryId: string;
}

export const RequestChangesModal: React.FC<RequestChangesModalProps> = ({
  isOpen,
  onClose,
  onSend,
  itineraryId,
}) => {
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSend = async () => {
    if (!note.trim() || !itineraryId) return;
    setIsSubmitting(true);
    try {
      await supabase
        .from('itineraries')
        .update({
          status: 'request_changes',
          note: note.trim(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', itineraryId);
      onSend?.();
    } finally {
      setIsSubmitting(false);
      setNote('');
      onClose();
    }
  };

  const handleClose = () => {
    setNote('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Request itinerary changes">
      <div className="space-y-4 p-6">
        <p className="text-sm text-gray-600">
          Need some changes to the itinerary? Write your requests here and we'll send you an updated proposal.
        </p>

        <div className="flex items-start gap-3 rounded-lg bg-gray-100 p-4">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-gray-500" />
          <p className="text-sm font-medium text-gray-700">
            Please note: once submitted, the current itinerary will no longer be available.
          </p>
        </div>

        <Textarea
          placeholder="Tell us what you'd like to change"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="min-h-[100px]"
        />

        <div className="flex justify-between pt-2">
          <Button variant="ghost" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSend} disabled={!note.trim() || isSubmitting}>
            Send request
          </Button>
        </div>
      </div>
    </Modal>
  );
};
